// src/utils/aggTuret.ts
//
// `content/{id}.agg` — İSTEMCİ tarafı türetme (saf; ağ/Firestore yok).
//
// ══════════════════════════════════════════════════════════════════════════
// NEDEN (ölçüm — docs/METASTABIL_COKUS_MEKANIZMASI.md, docs/AGG_SHARD_KARARI.md)
// ══════════════════════════════════════════════════════════════════════════
// `onRatingWrite` `agg`'ı **transaction** ile (oku→değiştir→yaz) güncelliyordu.
// Aynı doküman üzerindeki eşzamanlı transaction'lar birbirini ABORTED ettirip
// Admin SDK'nın 5 denemesini tüketiyor, oy `agg`'a HİÇ işlenmiyordu. Aynı
// makinede, aynı hızda (50 oy/sn, sınırsız eşzamanlılık) ölçüldü:
//     transaction'lı agg → abort %50–99,  e2e p99 ~21 sn, 120 oyun 106'sı kayıp
//     increment()'li yol → abort %0,      e2e p99 ~0,7 sn, kayıp YOK
// Bu yüzden yazma tarafı **değişmeli (commutative)** hâle getirildi:
// `count`, `sum`, `byStar.N` artık `FieldValue.increment()` ile yazılıyor.
//
// SONUCU BURAYI İLGİLENDİREN KISIM: `avg` (= sum/count) BÖLME içerir,
// dolayısıyla toplamsal DEĞİLDİR — okumadan yazılamaz. Bu yüzden sunucu artık
// `agg.avg`'ı YAZMIYOR; ortalama **okuma anında** burada türetiliyor.
//
// Dünya deseni (kararın dayanağı): Firestore'un resmi "distributed counters"
// rehberi sayaçları `increment()` ile yazdırır, ve Firestore'un KENDİ
// `AggregateField.average()`'ı da ortalamayı saklamaz — `sum`/`count`tan
// okuma anında böler. Instagram/Reddit sınıfı sistemlerde de beğeni/puan
// sayaçları commutative sayaçtır, ortalama okuma tarafında hesaplanır.
//
// ⚠️ ESKİ DOKÜMANLAR: `sum`/`count` ilk günden yazılıyordu → türetme onlarda
// da çalışır. Yine de `sum` hiç yazılmamış çok eski bir doküman için
// `avg × count` yedeği var (aşağıda). Saklı `avg` ARTIK OTORİTE DEĞİLDİR
// (bayatlar); yalnız o yedekte okunur.

/** `content/{id}.agg` ham (Firestore'dan gelen) şekli. */
export type HamAgg = {
  count?: unknown;
  sum?: unknown;
  avg?: unknown;
  bayes?: unknown;
  byStar?: unknown;
  /** Eski/alternatif adlar — okuyucular bunlara da bakıyordu. */
  total?: unknown;
  n?: unknown;
  average?: unknown;
};

/** Görüntülemeye hazır toplam. */
export type TuretilmisToplam = {
  count: number;
  sum: number;
  avg: number;
};

function sayi(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function sayiVarMi(v: unknown): boolean {
  return v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));
}

/**
 * Ham `agg`tan `{count, sum, avg}` türet.
 *
 * `avg` DAİMA `sum / count`tan hesaplanır — saklı `avg`/`average` yalnız
 * `sum` hiç yoksa (çok eski doküman) `sum`ı kurtarmak için kullanılır.
 * Negatif kırpma burada yapılır: `increment()` kırpma yapamaz (kırpmak okuma
 * ister), o yüzden kırpma okuma tarafına taşındı; saklı değerin onarımı
 * sunucudaki `reconcileAggFromRatings` ground-truth işinde.
 */
export function aggTuret(agg: HamAgg | null | undefined): TuretilmisToplam {
  if (!agg || typeof agg !== "object") return { count: 0, sum: 0, avg: 0 };

  const hamSayim = sayiVarMi(agg.count)
    ? agg.count
    : sayiVarMi(agg.total)
      ? agg.total
      : agg.n;
  const count = Math.max(0, sayi(hamSayim));

  const saklıAvg = sayiVarMi(agg.avg) ? agg.avg : agg.average;

  let sum: number;
  if (sayiVarMi(agg.sum)) {
    sum = Math.max(0, sayi(agg.sum));
  } else if (sayiVarMi(saklıAvg) && count > 0) {
    sum = Math.max(0, sayi(saklıAvg) * count);
  } else {
    sum = 0;
  }

  return { count, sum, avg: count > 0 ? sum / count : 0 };
}

/**
 * Ham `agg`ı, ESKİ okuyucuların beklediği şekle çevir: alanlar korunur,
 * `avg`/`count`/`sum` türetilmişle DEĞİŞTİRİLİR.
 *
 * Böylece `agg.avg` okuyan mevcut bileşenler (Post, PostMobile,
 * PostDetailModalDesktop, MyClips, ClipsDesktop, ProfilePostViewerMobile,
 * useClipEngagement) TEK NOKTADAN düzelir; her biri ayrı ayrı formül
 * taşımaz.
 */
export function aggNormalize<T extends HamAgg>(agg: T | null | undefined): (T & TuretilmisToplam) | null {
  // ── 🐞 BUGGIFY BURADA, `aggTuret`in İÇİNDE DEĞİL — ÖLÇÜLEREK TAŞINDI ────
  // İlk yazımda iki nokta `aggTuret` gövdesindeydi ve kapsama koşusunda 0/4
  // çıktı. Sebep ürün değil VERİ: `aggTuret` ilk satırda `if (!agg) return`
  // yapıyor, dünyanın fixture'ında ise `content/*` belgelerinde `agg` alanı
  // HİÇ YOK (henüz oy yazılmamış). Kanca doğru dosyadaydı ama ULAŞILAMAZ bir
  // dalın arkasındaydı. Çağıran (`usePostLogic`) `aggNormalize(d?.agg)`ı
  // KOŞULSUZ çağırıyor → nokta buraya taşındı ve "belgede agg YOK" hâlinin
  // kendisi enjeksiyonun girdisi oldu.
  // 🧭 Ders: bir noktanın ölü olması çoğu kez ÜRÜN hakkında değil DÜNYANIN
  //    VERİSİ hakkında bir şey söyler; hüküm vermeden önce sebebi okunmalı.

  // 🐞 BUGGIFY · i-agg-toplam-sisik — ÇİFT SAYILMIŞ AGG OKU.
  // Taklit edilen GERÇEK arıza SUNUCUDA ÖLÇÜLDÜ: `s-agg-cift-teslimat`
  // at-least-once teslimatta aynı deltayı iki kez uyguluyor → `sum` şişer,
  // ortalama 5★'ı aşabilir. SINANAN ONARIM: ortalamanın DAİMA `sum/count`tan
  // türetilmesi + tüketicilerdeki 0..5 kırpma; "7,4 ★" GÖSTERİLMEMELİ.
  // 🐞 BUGGIFY · i-agg-sayim-kayip — SAYIM DÜŞMÜŞ, TOPLAM KALMIŞ.
  // `agg.count` ve `agg.sum` AYRI `increment()` alanlarıdır; kısmi yazım
  // ikisini ayrıştırabilir. `sum>0, count=0` bir SIFIRA BÖLME adayıdır.
  // SINANAN ONARIM: `count > 0 ? sum / count : 0` → `NaN`/`Infinity` yok.
  // ⚙️ İkisi de üretimde derleme zamanı elenir.
  let ham: HamAgg | null | undefined = agg;
  if (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-agg-toplam-sisik")) {
    ham = { ...(agg || {}), count: 2, sum: 24 } as T;
  } else if (process.env.REACT_APP_BUGGIFY === "1" && window.__vwBuggify?.("i-agg-sayim-kayip")) {
    ham = { ...(agg || {}), count: 0, sum: 12 } as T;
  }
  if (!ham || typeof ham !== "object") return null;
  return { ...(ham as T), ...aggTuret(ham) };
}
